import React, { useEffect, useState } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { PencilSquare, Person } from "react-bootstrap-icons";
import EditPatientInfoModal from "./EditPatientInfoModal";
import { useDispatch, useSelector } from "react-redux";
import { GetAllDropDownData } from "features/eliglibility/eliglibility-check/config/eligibilityCheckApi";
import { GetAllNationality } from "features/preauthorization/config/preauthorizationApi";
import { handleFillStepData } from "../config/claimSlice";
import moment from "moment";

const PatientInfoCard = () => {
  const dispatch = useDispatch();
  const {
    genderList,
    maritalList,
    professionList,
    nationalityList,
    relationshipList,
    religionList,
    patientSearchData,
    identifierTypeList,
    insuranceList,
  } = useSelector((state) => state.eligibilityCheck);
  const { nationalityPreList } = useSelector((state) => state.preauthorization);
  const { stepFormData, claimRequestData, viewClaimInfo } = useSelector(
    (state) => state.claim
  );
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );

  const payloadClaim = viewClaimInfo?.payload_json;
  const status = viewClaimInfo?.claim_status;

  // Determine if the edit button should be disabled based on claim status
  const isDisabled = [
    "error",
    "ready to submit",
    "rejected",
    "cancelled",
    "in process",
    "validation error",
    "invalid",
  ].includes(status?.toLowerCase());

  const [showEditModal, setShowEditModal] = useState(false);

  // Extract patient and coverage data from claimRequestData
  const patientInfoReq = claimRequestData?.entry?.filter(
    (item) => item?.resource?.resourceType === "Patient"
  );

  const coverageReq = claimRequestData?.entry?.find(
    (e) => e?.resource?.resourceType === "Coverage"
  );
  const claimRequest = claimRequestData?.entry?.find(
    (e) => e?.resource?.resourceType === "Coverage"
  );

  const isNewBorn = claimRequest?.resource?.extension?.find((item) =>
    item?.url?.includes("extension-newborn")
  )?.valueBoolean;

  // Determine identifier type
  const iqamaDetails = patientInfoReq?.[0]?.resource?.identifier?.[0]?.system
    ?.toLowerCase()
    .includes("iqama")
    ? "Iqama"
    : patientInfoReq?.[0]?.resource?.identifier?.[0]?.system
      ?.toLowerCase()
      .includes("nationalid")
      ? "National ID"
      : null;

  // Extract MRN values
  const patientMRN =
    patientInfoReq?.[0]?.resource?.identifier?.find(
      (item) => item?.type?.coding?.[0]?.code === "MR"
    )?.value || payloadClaim?.patient_mrn;

  const beneficiaryMRN =
    patientInfoReq?.[1]?.resource?.identifier?.find(
      (item) => item?.type?.coding?.[0]?.code === "MR"
    )?.value || patientInfoReq?.[1]?.resource?.identifier?.[0]?.value;

  // Extract occupation
  const occupation =
    patientInfoReq?.[0]?.resource?.extension?.find((item) =>
      item?.url?.includes("occupation")
    )?.valueCodeableConcept?.coding?.[0]?.code ||
    payloadClaim?.patient_profession;

  // Extract membership and policy numbers
  const membershipNo =
    coverageReq?.resource?.identifier?.[0]?.value ||
    payloadClaim?.patient_memid;
  const policyNo = payloadClaim?.patient_plynum;

  const idReuse = patientInfoReq?.[0]?.resource?.identifier?.[0]?.value;

  // Extract names
  const fullName = patientInfoReq?.[0]?.resource?.name?.[0] || {
    text: `${payloadClaim?.patient_finame || ""} ${payloadClaim?.patient_faname || ""
      }`,
    family: payloadClaim?.patient_faname || "",
  };
  const beneficiaryFullName = patientInfoReq?.[1]?.resource?.name?.[0] || {};
  const mobileNo = patientInfoReq?.[0]?.resource?.telecom?.[0]?.value;

  // Extract policy holder details
  const policyHolderDetails = claimRequestData?.entry?.find(
    (item) =>
      item?.resource?.resourceType === "Organization" &&
      item?.resource?.meta?.profile?.some(
        (profile) =>
          profile ===
          "http://nphies.sa/fhir/ksa/nphies-fs/StructureDefinition/policyholder-organization|1.0.0"
      )
  );

  // Function to find class value
  const findClassValue = (code) => {
    return (
      coverageReq?.resource?.class?.find((cls) =>
        cls?.type?.coding?.some((c) => c?.code === code)
      )?.value ||
      payloadClaim?.patient_clsnme ||
      "-"
    );
  };

  const religionEig = patientInfoReq?.[0]?.resource?.extension?.find((item) =>
    item?.url?.includes("religion")
  )?.valueCodeableConcept?.coding?.[0]?.code;
  // Prepare info items for display
  const infoItems = [
    {
      label: "Full Name",
      value:
        `${stepFormData?.first_name || payloadClaim?.patient_finame || ""} ${stepFormData?.family_name || payloadClaim?.patient_faname || ""
          }`.trim() || "-",
    },
    {
      label: "MRN",
      value: stepFormData?.patient_mrn || patientMRN || "-",
    },
    {
      label: "Identifier Type",
      value:
        stepFormData?.identifier_type?.ident_type_name || iqamaDetails || "-",
    },
    {
      label: "ID",
      value: stepFormData?.id || payloadClaim?.patient_natid || "-",
    },
    {
      label: "Membership No",
      value: stepFormData?.membership_no || membershipNo || "-",
    },
    {
      label: "Policy No",
      value: stepFormData?.policy_no || policyNo || "-",
    },
    {
      label: "Policy Holder Name",
      value:
        stepFormData?.policy_holder_name ||
        policyHolderDetails?.resource?.name ||
        payloadClaim?.patient_polcyholdr ||
        "-",
    },
    {
      label: "Class Name",
      value: stepFormData?.class_name || findClassValue("group") || "-",
    },
    {
      label: "Gender",
      value:
        stepFormData?.gender?.genders_display ||
        genderList?.find(
          (item) => item?.genders_code === payloadClaim?.patient_gender
        )?.genders_display ||
        payloadClaim?.patient_gender ||
        "-",
    },
    {
      label: "Relationship",
      value:
        stepFormData?.relationship?.patrtl_name ||
        relationshipList?.find(
          (item) => item?.patrtl_code === payloadClaim?.patient_relation
        )?.patrtl_name ||
        payloadClaim?.patient_relation ||
        "-",
    },
    {
      label: "Birth Date",
      value: stepFormData?.birth_date
        ? moment(stepFormData?.birth_date).format("DD-MM-YYYY")
        : payloadClaim?.patient_dob
          ? moment(payloadClaim?.patient_dob).format("DD-MM-YYYY")
          : "-",
    },
    {
      label: "Marital Status",
      value:
        stepFormData?.marital_status?.maritalstatus_disply ||
        maritalList?.find(
          (item) => item?.maritalstatus_code === payloadClaim?.patient_marital
        )?.maritalstatus_display ||
        "-",
    },
    {
      label: "Occupation",
      value:
        stepFormData?.occupation?.occup_display ||
        professionList?.find(
          (item) => item?.occup_code === payloadClaim?.patient_profession
        )?.occup_display ||
        payloadClaim?.patient_profession ||
        "-",
    },
  ];

  useEffect(() => {
    if (!payloadClaim && !patientInfoReq?.[0] && !coverageReq) return;

    // Extract name details
    const fullText =
      fullName?.text?.trim() ||
      `${payloadClaim?.patient_finame || ""} ${payloadClaim?.patient_faname || ""
        }`.trim();
    const beneficiaryFullText = beneficiaryFullName?.text?.trim() || "";
    const familyName =
      fullName?.family?.trim() || payloadClaim?.patient_faname || "";
    const benFamilyName = beneficiaryFullName?.family?.trim() || "";

    const nameParts = fullText?.split(" ").filter(Boolean);
    const benNameParts = beneficiaryFullText?.split(" ").filter(Boolean);

    const familyIndex = nameParts?.lastIndexOf(familyName);
    const benFamilyIndex = benNameParts?.lastIndexOf(benFamilyName);

    const firstName =
      familyIndex !== -1 && nameParts
        ? nameParts.slice(0, familyIndex).join(" ")
        : fullText || payloadClaim?.patient_finame || "";
    const benFirstName =
      benFamilyIndex !== -1 && benNameParts
        ? benNameParts.slice(0, benFamilyIndex).join(" ")
        : beneficiaryFullText || "";

    // Dispatch data to Redux store
    dispatch(
      handleFillStepData({
        check: "first_name",
        value: firstName || payloadClaim?.patient_finame || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "family_name",
        value: familyName || payloadClaim?.patient_faname || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "child_first_name",
        value: benFirstName || payloadClaim?.patient_benf_finame || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "child_family_name",
        value: benFamilyName || payloadClaim?.patient_benf_faname || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "identifier_type",
        value:
          identifierTypeList?.find(
            (item) =>
              item?.ident_type_name === iqamaDetails ||
              item?.ident_type_id === payloadClaim?.idenf_type
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "id",
        value: idReuse || payloadClaim?.patient_natid || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "id_type",
        value: 1,
      })
    );
    dispatch(
      handleFillStepData({
        check: "membership_no",
        value: membershipNo || payloadClaim?.patient_memid || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "policy_no",
        value: policyNo || payloadClaim?.patient_plynum || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "mobile_no",
        value: mobileNo || payloadClaim?.patient_mobile || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "patient_mrn",
        value: patientMRN || payloadClaim?.patient_mrn || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "policy_holder_name",
        value:
          policyHolderDetails?.resource?.name ||
          payloadClaim?.patient_polcyholdr ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "class_name",
        value: findClassValue("group") || payloadClaim?.patient_clsnme || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "gender",
        value:
          genderList?.find(
            (item) =>
              item?.genders_code === patientInfoReq?.[0]?.resource?.gender ||
              item?.genders_code === payloadClaim?.patient_gender
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "relation",
        value:
          genderList?.find(
            (item) =>
              item?.genders_code === patientInfoReq?.[0]?.resource?.gender ||
              item?.genders_code === payloadClaim?.patient_gender
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "child_gender",
        value:
          genderList?.find(
            (item) =>
              item?.genders_code === patientInfoReq?.[1]?.resource?.gender ||
              item?.genders_code === payloadClaim?.patient_benf_gender
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "relationship",
        value: relationshipList?.find(
          (item) =>
            item?.patrtl_code === payloadClaim?.patient_relation ||
            item?.patrtl_name === payloadClaim?.patient_relation
        ),
      })
    );
    dispatch(
      handleFillStepData({
        check: "marital_status",
        value:
          maritalList?.find(
            (item) =>
              item?.maritalstatus_code ===
              patientInfoReq?.[0]?.resource?.maritalStatus?.coding[0]?.code ||
              item?.maritalstatus_code === payloadClaim?.patient_marital
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "occupation",
        value:
          professionList?.find(
            (item) =>
              item?.occup_code === occupation ||
              item?.occup_code === payloadClaim?.patient_profession
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "religion",
        value:
          religionList?.find(
            (item) =>
              item?.religion_code === religionEig ||
              item?.religion_code === payloadClaim?.patient_religion
          ) || null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "birth_date",
        value:
          patientInfoReq?.[0]?.resource?.birthDate ||
          payloadClaim?.patient_dob ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "child_birth_date",
        value:
          patientInfoReq?.[1]?.resource?.birthDate ||
          payloadClaim?.patient_benf_dob ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "new_born",
        value: payloadClaim?.new_brn || isNewBorn,
      })
    );
    dispatch(
      handleFillStepData({
        check: "child_mrn",
        value: payloadClaim?.patient_benf_mrn || beneficiaryMRN || "",
      })
    );
  }, [
    coverageReq,
    claimRequestData,
    identifierTypeList,
    genderList,
    maritalList,
    professionList,
    relationshipList,
    religionList,
    viewClaimInfo,
    payloadClaim,
    dispatch,
  ]);

  useEffect(() => {
    if (identifierTypeList?.length === 0 || nationalityPreList?.length === 0) {
      dispatch(GetAllDropDownData());
      dispatch(GetAllNationality());
    }
  }, [dispatch, identifierTypeList, nationalityPreList]);

  console.log({ stepFormData });

  return (
    <>
      <div>
        <Card.Header className="d-flex badge-subtle-primary justify-content-between border-primary border-bottom align-items-center text-primary">
          <div className="fw-bold d-flex align-items-center flex-nowrap">
            <Person className="me-2" size={12} />
            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <span className="fs-12">Patient Information</span>
            </div>
          </div>

          {isDisabled && Number(claimsSetting?.submit) === 1 && (
            <PencilSquare
              onClick={() => setShowEditModal(true)}
              role="button"
              className="rounded p-0"
              size={12}
            />
          )}
        </Card.Header>
        <Card.Body className="p-2">
          <Row>
            {infoItems.map((item, index) => (
              <Col
                key={index}
                xs={12}
                sm={6}
                className="d-flex justify-content-between flex-column mb-1"
              >
                <div className="text-800 fw-bold fs-12">{item.label}</div>
                <div className="fw-semibold text-capitalize text-600 fs-12">
                  {item.value}
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </div>
      <EditPatientInfoModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
      />
    </>
  );
};

export default PatientInfoCard;
